// @ts-nocheck
import find from 'lodash/find';
import kebabCase from 'lodash/kebabCase';
import formatter from 'app/shared/utils/formatter';
import toLower from 'lodash/toLower';
import trim from 'lodash/trim';

import allStates from 'app/shared/utils/states.json';

/**
 * Configuration for city sitemap feature
 * List of states where city sitemaps are enabled for SEO testing (by abbreviation)
 */
export const CITY_SITEMAP_ENABLED_STATES = new Set([
  'AL', // Alabama
  'AZ', // Arizona
  'CA', // California
  'CO', // Colorado
  'CT', // Connecticut
  'HI', // Hawaii
  'IL', // Illinois
  'IN', // Indiana
  'KY', // Kentucky
  'MA', // Massachusetts
  'MD', // Maryland
  'MI', // Michigan
  'MN', // Minnesota
  'MO', // Missouri
  'NC', // North Carolina
  'NH', // New Hampshire
  'NJ', // New Jersey
  'OH', // Ohio
  'OR', // Oregon
  'PA', // Pennsylvania
  'SC', // South Carolina
  'TN', // Tennessee
  'VT', // Vermont
  'WI', // Wisconsin
  'WV', // West Virginia
]);

/**
 * Checks if city sitemaps are enabled for a given state
 * @param {string} stateAbbreviation - State abbreviation (e.g., "CA", "NY")
 * @returns {boolean} true if city sitemaps are enabled for this state
 */
export const isCitySitemapEnabled = (stateAbbreviation) => {
  if (!stateAbbreviation) return false;
  return CITY_SITEMAP_ENABLED_STATES.has(stateAbbreviation.toUpperCase());
};

/**
 * Extracts state abbreviation from a city resourceId
 * @param {string} resourceId - City resourceId (e.g., "san-francisco-ca", "new-york-ny")
 * @returns {string|null} State abbreviation (e.g., "ca", "ny") or null if not found
 */
export const extractStateAbbreviationFromResourceId = (resourceId) => {
  if (!resourceId || typeof resourceId !== 'string') return null;

  // Look for common state abbreviation patterns at the end
  const match = resourceId.match(/-([a-z]{2})$/);
  return match ? match[1] : null;
};

/**
 * Maps state abbreviation to full state name
 * @param {string} stateAbbr - State abbreviation (e.g., "ca", "ny")
 * @returns {string|null} Full state name (e.g., "california", "new-york") or null if not found
 */
export const getStateNameFromAbbreviation = (stateAbbr) => {
  if (!stateAbbr) return null;

  const stateData = find(allStates, ({ abbreviation }) => {
    return toLower(stateAbbr) === toLower(abbreviation);
  });

  return stateData ? stateData.resourceId : null;
};

/**
 * Extracts city name from resourceId by removing the state suffix
 * @param {string} resourceId - City resourceId (e.g., "san-francisco-ca")
 * @returns {string|null} City name (e.g., "san-francisco") or null if invalid
 */
export const extractCityNameFromResourceId = (resourceId) => {
  if (!resourceId || typeof resourceId !== 'string') return null;

  const stateAbbr = extractStateAbbreviationFromResourceId(resourceId);
  if (!stateAbbr) return null;

  // Remove the state suffix (e.g., "san-francisco-ca" -> "san-francisco")
  return resourceId.replace(`-${stateAbbr}`, '');
};

/**
 * Generates a city sitemap URL from a city resourceId
 * @param {string} resourceId - City resourceId (e.g., "san-francisco-ca")
 * @returns {string|null} URL like "/california/san-francisco" or null if invalid
 */
export const generateCitySitemapUrlFromResourceId = (resourceId) => {
  const stateAbbr = extractStateAbbreviationFromResourceId(resourceId);
  const cityName = extractCityNameFromResourceId(resourceId);
  const stateName = getStateNameFromAbbreviation(stateAbbr);

  if (!cityName || !stateName) return null;

  return `/${stateName}/${cityName}`;
};

/**
 * Generates a city sitemap URL from city and state names (legacy method)
 * @param {string} city - City name (e.g., "los-angeles" or "Los Angeles")
 * @param {string} state - State name (e.g., "california" or "California")
 * @returns {string} URL like "/california/los-angeles"
 */
export const generateCitySitemapUrl = (city, state) => {
  const citySlug = kebabCase(city);
  const stateSlug = kebabCase(state);
  return `/${stateSlug}/${citySlug}`;
};

/**
 * Formats a kebab-case city name for display
 * @param {string} kebabCaseName - Kebab-case city name (e.g., "los-angeles")
 * @returns {string} Proper case city name (e.g., "Los Angeles")
 */
export const formatCityNameForDisplay = formatter.string.kebabToDisplayCase;

/**
 * Converts city and state names to a resourceId format
 * @param {string} cityName - City name (e.g., "san-francisco")
 * @param {string} stateName - State name (e.g., "california")
 * @returns {string|null} ResourceId (e.g., "san-francisco-ca") or null if invalid
 */
export const generateResourceIdFromCityState = (cityName, stateName) => {
  if (!cityName || !stateName) return null;

  // Sanitize inputs
  const sanitizedCity = sanitizeSlug(cityName, 2, 50);
  const sanitizedState = sanitizeSlug(stateName, 2, 20);

  if (!sanitizedCity || !sanitizedState) return null;

  const stateData = find(allStates, ({ resourceId }) => {
    return sanitizedState === toLower(resourceId);
  });

  if (!stateData) return null;

  // Generate resourceId and validate format
  const resourceId = `${sanitizedCity}-${toLower(stateData.abbreviation)}`;

  // Additional validation for resourceId format
  if (resourceId.length > 75 || !resourceId.match(/^[a-z0-9-]+$/)) {
    return null;
  }

  return resourceId;
};

/**
 * Sanitizes and validates a URL slug
 * @param {string} slug - URL slug to validate
 * @param {number} minLength - Minimum allowed length
 * @param {number} maxLength - Maximum allowed length
 * @returns {string|null} Sanitized slug or null if invalid
 */
const sanitizeSlug = (slug, minLength = 2, maxLength = 50) => {
  if (!slug || typeof slug !== 'string') return null;

  // Remove any potentially dangerous characters and normalize
  const sanitized = slug
    .toLowerCase()
    .trim()
    // Allow only alphanumeric characters and hyphens
    .replace(/[^a-z0-9-]/g, '')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');

  // Validate length
  if (sanitized.length < minLength || sanitized.length > maxLength) {
    return null;
  }

  // Prevent empty or just hyphens
  if (!sanitized || sanitized === '-') {
    return null;
  }

  return sanitized;
};

/**
 * Generates the SRP (Search Results Page) URL for a city
 * @param {string} citySlug - City slug (e.g., "los-angeles")
 * @param {string} stateAbbreviation - State abbreviation (e.g., "ca")
 * @returns {string} SRP URL (e.g., "/los-angeles-ca/apartments-for-rent")
 */
export const generateCitySrpUrl = (citySlug, stateAbbreviation) => {
  if (!citySlug || !stateAbbreviation) return null;

  const resourceId = `${citySlug}-${stateAbbreviation.toLowerCase()}`;
  return `/${resourceId}/apartments-for-rent`;
};

/**
 * Parses a city sitemap path and returns city/state info
 * @param {string} path - URL path like "/california/san-francisco"
 * @returns {object|null} Object with city and state info, or null if invalid
 */
export const parseCitySitemapPath = (path = '') => {
  const cleanPath = trim(path, '/ ');
  const segments = cleanPath.split('/');

  if (segments.length !== 2) {
    return null;
  }

  const [rawStateSlug, rawCitySlug] = segments;

  // Sanitize and validate input
  const stateSlug = sanitizeSlug(rawStateSlug, 2, 20);
  const citySlug = sanitizeSlug(rawCitySlug, 2, 50);

  if (!stateSlug || !citySlug) {
    return null;
  }

  // Validate the state exists
  const stateData = find(allStates, ({ resourceId }) => {
    return stateSlug === toLower(resourceId);
  });

  if (!stateData) {
    return null;
  }

  // Check if city sitemaps are enabled for this state
  if (!isCitySitemapEnabled(stateData.abbreviation)) {
    return null;
  }

  // Generate the resourceId for API calls
  const resourceId = generateResourceIdFromCityState(citySlug, stateSlug);

  return {
    citySlug,
    stateSlug,
    cityName: formatCityNameForDisplay(citySlug),
    stateName: stateData.name,
    stateAbbreviation: stateData.abbreviation,
    resourceId,
    stateData,
  };
};
